import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { useRootStore } from "../../../../store/contexts";
import { ShowNumber } from "../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type BotPhaseViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  phaseNumberValue?: { displayId: string; id: string } | null;
  phaseNumber: number;
  phaseIdValue?: { displayId: string; id: string } | null;
  phaseId: string;
  phaseNameValue?: { displayId: string; id: string } | null;
  phaseName: string;
  statusValue?: { displayId: string; id: string } | null;
  status: string;
  stepsTotalValue?: { displayId: string; id: string } | null;
  stepsTotal: number;
  stepsCompletedValue?: { displayId: string; id: string } | null;
  stepsCompleted: number;
  startedAtValue?: { displayId: string; id: string } | null;
  startedAt: Date | null;
  completedAtValue?: { displayId: string; id: string } | null;
  completedAt: Date | null;
  durationValue?: { displayId: string; id: string } | null;
  duration: number | undefined;
  errorValue?: { displayId: string; id: string } | null;
  error: string;
  stepsLogPathValue?: { displayId: string; id: string } | null;
  stepsLogPath: string;
  screenshotFiles: CrudRows;
  screenshotViewAction?: IMenuAction[];
  stepsRows: CrudRows;
  stepsVisibleColumns: GridColumn[];
  stepsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
};

export const BotPhaseViewForm: React.FC<BotPhaseViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    phaseNumber = 0,
    phaseId = "",
    phaseName = "",
    status = "",
    stepsTotal = 0,
    stepsCompleted = 0,
    startedAt = null,
    completedAt = null,
    duration = 0,
    error = "{}",
    stepsLogPath = "",
    screenshotFiles,
    screenshotViewAction,
    stepsRows,
    stepsVisibleColumns,
    stepsSetColumnWiths,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="null"
            title={langStore.groupBotPhaseLabel}
            collapsible={false}
          >
            <FormControl fullWidth>
              <ShowNumber
                value={phaseNumber}
                label={langStore.BotPhasePhaseNumberLabel}
              />
              <ShowMultiLine
                value={phaseId}
                label={langStore.BotPhasePhaseIdLabel}
              />
              <ShowMultiLine
                value={phaseName}
                label={langStore.BotPhasePhaseNameLabel}
              />
              <ShowMultiLine
                value={status}
                label={langStore.BotPhaseStatusLabel}
              />
              <ShowNumber
                value={stepsTotal}
                label={langStore.BotPhaseStepsTotalLabel}
              />
              <ShowNumber
                value={stepsCompleted}
                label={langStore.BotPhaseStepsCompletedLabel}
              />
              <ShowDateTime
                value={startedAt}
                label={langStore.BotPhaseStartedAtLabel}
              />
              <ShowDateTime
                value={completedAt}
                label={langStore.BotPhaseCompletedAtLabel}
              />
              <ShowNumber
                value={duration}
                label={langStore.BotPhaseDurationLabel}
              />
              <InputCodeEditor
                value={error}
                onChange={() => {}}
                label={langStore.BotPhaseErrorLabel}
                language="json"
                readOnly={true}
              />
              <ShowMultiLine
                value={stepsLogPath}
                label={langStore.BotPhaseStepsLogPathLabel}
              />
              <ShowFilesCompact
                rows={screenshotFiles}
                showLabel={true}
                label={langStore.BotPhaseScreenshotLabel}
                menuActions={screenshotViewAction}
                columns={[
                  { name: "mimetype", title: langStore.fileTypeLabel },
                  { name: "name", title: langStore.fileNameLabel },
                  { name: "formattedSize", title: langStore.fileSizeLabel },
                ]}
              />
              <ShowItemsCompact
                rows={stepsRows}
                showLabel={true}
                label={langStore.BotPhaseStepsLabel}
                columns={stepsVisibleColumns}
                setColumnWidths={stepsSetColumnWiths}
                entityName="BotStep"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("botStep", rowId)
                }
              />
            </FormControl>
          </GroupContainer>
        </Box>
      </Grid>
    );
  }
);
